/*
 * Page de pause (mode maintenance).
 * Affichée aux visiteuses quand l'interrupteur admin est activé pendant une
 * intervention. Le message et l'heure estimée de retour sont personnalisables
 * depuis la page admin. Un compte à rebours doux s'affiche si une heure de
 * retour est renseignée. L'espace admin reste toujours accessible.
 */

import { useEffect, useState } from "react";
import { BellRing, Clock3, Flower2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { submitMaintenanceNotify } from "@/lib/api";

const DEFAULT_MESSAGE =
  "CYCLIQ est en pause quelques instants pour une amélioration. Reviens très vite, tout sera prêt.";

/* Libellé français de l'heure de retour : aujourd'hui à HH:MM ou date complète. */
const formatResumeLabel = (resumeDate) => {
  const now = new Date();
  const time = resumeDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (resumeDate.toDateString() === now.toDateString()) {
    return `aujourd'hui à ${time}`;
  }
  const day = resumeDate.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return `${day} à ${time}`;
};

/* Durée restante lisible : environ X h YY min, X min ou moins d'une minute. */
const formatRemaining = (ms) => {
  const totalMinutes = Math.round(ms / 60000);
  if (totalMinutes < 1) return "moins d'une minute";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `environ ${hours} h ${String(minutes).padStart(2, "0")} min`;
  return `environ ${minutes} min`;
};

const ResumeInfo = ({ resumeAt }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const resumeDate = resumeAt ? new Date(resumeAt) : null;
  if (!resumeDate || Number.isNaN(resumeDate.getTime())) return null;
  const remaining = resumeDate.getTime() - now;

  return (
    <div
      className="mt-5 rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.45)] px-4 py-3"
      data-testid="maintenance-resume-info"
    >
      <div className="flex items-center justify-center gap-1.5 text-[#235277]">
        <Clock3 className="w-3.5 h-3.5" aria-hidden="true" />
        <p className="text-[11px] font-semibold uppercase tracking-wide">Retour estimé</p>
      </div>
      {remaining > 0 ? (
        <>
          <p className="mt-1 font-heading text-base font-semibold text-[#235277]">
            {formatResumeLabel(resumeDate)}
          </p>
          <p className="text-xs text-[#333333]" data-testid="maintenance-countdown" aria-live="polite">
            soit dans {formatRemaining(remaining)}
          </p>
        </>
      ) : (
        <p
          className="mt-1 font-heading text-base font-semibold text-[#235277]"
          data-testid="maintenance-countdown"
        >
          Le retour est imminent
        </p>
      )}
    </div>
  );
};

/*
 * Formulaire de notification de retour : la visiteuse laisse son adresse
 * pour etre prevenue automatiquement des la fin de la pause. Usage unique
 * et RGPD : l'adresse est supprimee immediatement apres l'envoi.
 * En mode apercu (admin), la soumission est simulee sans appel reseau.
 */
const NotifyForm = ({ previewMode }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === "sending") return;
    setError("");
    if (previewMode) {
      setFeedback("Aperçu : en vrai, l'inscription serait bien enregistrée.");
      setStatus("done");
      return;
    }
    setStatus("sending");
    try {
      const result = await submitMaintenanceNotify(email.trim());
      setFeedback(result.message || "C'est noté : tu seras prévenue dès le retour.");
      setStatus("done");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Inscription impossible. Réessaie dans un instant.");
      setStatus("idle");
    }
  };

  if (status === "done") {
    return (
      <div
        className="mt-5 rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.45)] px-4 py-3"
        data-testid="maintenance-notify-success"
      >
        <p className="text-sm font-medium text-[#235277]">{feedback}</p>
        <p className="mt-1 text-[11px] text-[#9E9E9E]">
          Ton adresse sera utilisée une seule fois puis supprimée aussitôt.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-5 text-left" data-testid="maintenance-notify-form">
      <div className="flex items-center gap-1.5 text-[#235277]">
        <BellRing className="w-3.5 h-3.5" aria-hidden="true" />
        <p className="text-xs font-semibold">Être prévenue du retour</p>
      </div>
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <Input
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="ton.adresse@email.fr"
          data-testid="maintenance-notify-email-input"
          className="h-11 rounded-xl border-[rgba(35,82,119,0.18)] text-sm"
          aria-invalid={Boolean(error)}
        />
        <Button
          type="submit"
          disabled={status === "sending"}
          data-testid="maintenance-notify-submit-button"
          className="h-11 rounded-xl bg-[#235277] text-white hover:bg-[#1B415F] font-heading font-semibold px-5 transition-colors duration-200"
        >
          {status === "sending" ? "Envoi" : "Me prévenir"}
        </Button>
      </div>
      {error ? (
        <p className="mt-2 text-xs text-[#8C2F2B]" role="alert" data-testid="maintenance-notify-error">
          {error}
        </p>
      ) : null}
      <p className="mt-2 text-[11px] leading-relaxed text-[#9E9E9E]">
        Un seul email, dès la fin de la pause. Ton adresse est ensuite supprimée
        immédiatement, elle ne sert à rien d'autre.
      </p>
    </form>
  );
};

export const MaintenancePage = ({ message, resumeAt, logoUrl, onRetry, checking, previewMode = false }) => (
  <div
    data-testid="maintenance-page"
    className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#F9FEFF]"
  >
    <div className="w-full max-w-[440px] text-center">
      <div className="rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] shadow-[var(--shadow-md)] px-8 py-10">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Gino Body & Soul"
            className="h-10 mx-auto mb-6"
            data-testid="maintenance-logo"
          />
        ) : null}
        <div
          className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E2F6FF]"
          aria-hidden="true"
        >
          <Flower2 className="h-6 w-6 text-[#235277]" />
        </div>
        <h1 className="font-heading mt-5 text-xl font-semibold tracking-tight text-[#060606]">
          On prend soin de CYCLIQ
        </h1>
        <p
          className="mt-3 text-sm leading-relaxed text-[#333333]"
          data-testid="maintenance-message"
        >
          {message || DEFAULT_MESSAGE}
        </p>
        <ResumeInfo resumeAt={resumeAt} />
        <NotifyForm previewMode={previewMode} />
        <p className="mt-4 text-xs leading-relaxed text-[#9E9E9E]">
          Merci de ta patience. Aucune donnée n'est perdue et rien n'est à refaire.
        </p>
        <Button
          onClick={onRetry}
          disabled={checking}
          variant="outline"
          data-testid="maintenance-retry-button"
          className="mt-6 min-h-[44px] rounded-xl border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-heading font-semibold transition-colors duration-200"
        >
          <RefreshCw className={`mr-2 w-4 h-4 ${checking ? "animate-spin" : ""}`} aria-hidden="true" />
          {checking ? "Vérification" : "Vérifier à nouveau"}
        </Button>
      </div>
      <p className="mt-5 text-[11px] text-[#9E9E9E]">Gino Body & Soul</p>
    </div>
  </div>
);
